import { GetCompanyResponseDto } from '../../../services/dtos/GetCompanyResponseDto';
import { FC } from 'react';
import {
  Box,
  Stack,
  Tooltip,
  Typography,
  capitalize,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';

import { CalendarMonth, Flag, People } from '@mui/icons-material';
import truncate from 'lodash.truncate';

import { useAuth } from '../../../context/Auth';
import { CompanyLogo } from '../../CompanyLogo';
import { parseJSON } from 'date-fns';
import Link from 'next/link';
import { routes } from '../../../config/routes';
import { isNewListing } from '../../../utils/constants';
import { NewListingMark } from '../company-profile/NewListingMark';
import { ManufacturingLocations } from './ManufacturingLocations';
import { VerifiedCompanyIcon } from './VerifiedCompanyIcon';
import { CertificationsOrMembershipsCompanyIcon } from './CertificationsOrMembershipsCompanyIcon';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { removeHTMLFromString } from '../../../utils/removeHtmlFromString';
import { StyledDivider } from '../../StyledDivider';
import { ListItemCard } from '../../ListItemCard';
import { CategoryIconsRow } from '../../CategoryIconsRow';

interface Props {
  company: GetCompanyResponseDto;
}

export const CompanyCard: FC<Props> = ({ company }) => {
  const { t } = useTranslation([
    'directory',
    'purchase-announcements',
    'common',
  ]);
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const rowItems1 = [];
  const rowItems2 = [];

  if (
    company?.caCompany?.presenceCountries &&
    company?.caCompany?.presenceCountries.length > 0
  ) {
    rowItems1.push(
      <ManufacturingLocations
        presenceCountries={company.caCompany.presenceCountries}
      />
    );
  }

  if (
    company.caCompany.textiles_category === 'Manufacturer' &&
    company.caCompany.employees_number &&
    Number(company.caCompany.employees_number) > 0
  ) {
    rowItems2.push(
      <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <People />
        {company.caCompany.employees_number} {t('directory:company:employees')}
      </Typography>
    );
  }

  if (company.caCompany.year_established) {
    rowItems2.push(
      <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarMonth />{' '}
        {t('directory:company:establishedIn', {
          year: company.caCompany.year_established,
        })}
      </Typography>
    );
  }

  if (isNewListing(parseJSON(company.company.createdAt))) {
    rowItems2.push(<NewListingMark createdAt={company.company.createdAt} />);
  }

  return (
    <ListItemCard isBlurred={!isAuthenticated}>
      <Link
        href={
          isAuthenticated
            ? routes.COMPANY_PROFILE_BY_ID(company.company.id)
            : routes.SIGN_IN(routes.COMPANY_PROFILE_BY_ID(company.company.id))
        }
      >
        <Box sx={{ position: 'absolute', right: 16 }}>
          <VerifiedCompanyIcon company={company} />
          <CertificationsOrMembershipsCompanyIcon company={company} />
        </Box>
        <Box display="flex" gap={2}>
          <Box display="flex" alignItems="center">
            <CompanyLogo
              url={company.caCompany.photo_url || company.caCompany.picture}
            />
          </Box>

          <Box display={'flex'} flexDirection={'column'} sx={{ flex: 1 }}>
            <Typography variant="h5">{company.caCompany.name}</Typography>

            <Typography variant="h6">
              {t(`common:${company.caCompany.textiles_category}`)}
            </Typography>
            <Typography variant="body1">
              {removeHTMLFromString(
                truncate(company.caCompany.bussiness_description, {
                  length: 100,
                })
              )}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          {company.company.categoryIcons &&
          company.company.categoryIcons.length > 0 ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography>{t('directory:company:produces')} </Typography>
              <Stack
                direction={smUp ? 'row' : 'column'}
                spacing={1}
                divider={smUp ? <StyledDivider /> : null}
                alignItems={smUp ? 'center' : 'flex-start'}
              >
                {company.company.categoryIcons.map((icon) => {
                  const title = icon.split('/').pop().split('.')[0];
                  return (
                    <Tooltip key={icon} title={capitalize(title)}>
                      <Box position="relative" width={30} height={30}>
                        <Image
                          src={icon}
                          alt={t('directory:company:product')}
                          fill
                        />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Stack>
            </Box>
          ) : null}
          <Stack
            mt={1}
            direction={smUp ? 'row' : 'column'}
            spacing={1}
            divider={smUp ? <StyledDivider /> : null}
            alignItems={smUp ? 'center' : 'flex-start'}
          >
            {rowItems1}
          </Stack>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack
              direction={smUp ? 'row' : 'column'}
              spacing={1}
              divider={smUp ? <StyledDivider /> : null}
              alignItems={smUp ? 'center' : 'flex-start'}
            >
              {rowItems2}
            </Stack>
          </Box>
          <Stack direction="row" justifyContent="flex-start" spacing={1}>
            <CategoryIconsRow company={company} />
          </Stack>
        </Box>
      </Link>
    </ListItemCard>
  );
};
